import { Component, ElementRef, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform, base64ToFile } from 'ngx-image-cropper';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { catchError, finalize, first, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { LicenseDataService } from 'src/app/new-license-scan/license-data.service';
import { ImageCompressService } from 'src/app/shared/utils/image-compress.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { Location } from '@angular/common';
import { FileDataService } from 'src/app/shared/file-data/file-data.service';
import { DocumentExtractDataService } from 'src/app/shared/services/document-extract-data.service';
import { environment } from 'src/environments/environment';
import * as mime from 'mime';
import { DriverQualificationService } from 'src/app/driver-qualification-files/service/driver-qualification.service';
import { getDocumentFileExtension } from 'src/app/shared/utils/document-file-extension.util';

const FRONT_LICENSE_DEFAULT_NAME = 'front-license';
const BACK_LICENSE_DEFAULT_NAME = 'back-license';
const MAX_FILE_SIZE = 2.5 * 1024 * 1024; // 5MB

@Component({
    selector: 'app-license-upload',
    templateUrl: './license-upload.component.html',
    styleUrls: ['./license-upload.component.scss'],
    standalone: false
})
export class LicenseUploadComponent implements OnDestroy {
  @ViewChild('previewImage') previewImage: ImageCropperComponent;
  @ViewChild('fileInput') fileInput: ElementRef;

  // Image states
  documentPicture: File;
  loading = false;
  showCrop = false;
  imageChangedEvent: any;
  acceptTypesPicture = 'image/png,image/jpg,image/jpeg';
  imageFormat: string;

  // Cropper properties
  canvasRotation = 0;
  transform: ImageTransform = {};
  private rotationTimeout: any;
  isRotating = false;
  cropperPosition = {
    x1: 100,
    y1: 100,
    x2: 460,
    y2: 260
  };

  // API and UI states
  isApiLoading = false;
  isFrontSave = false;
  isPreviewLoading = false;
  isExtracting = false; // Document Extract loading state
  extractedLicenseData: any = null; // Store extraction results

  // Image data
  finalImageFiles: File[] = [];
  frontImage: File;
  backImage: File;
  frontImageUrl: SafeUrl;
  backImageUrl: SafeUrl;
  capturedImage: File; // Add this to match profile component
  capturedImageUrl: SafeUrl;

  // Preview loading tracking
  private imageLoadCount = 0;
  private readonly PREVIEW_IMAGE_MAX_SIZE = 800; // Maximum dimension in pixels

  constructor(
    private readonly router: Router,
    private readonly domSanitizer: DomSanitizer,
    private readonly licenseDataService: LicenseDataService,
    private readonly registrationService: RegistrationService,
    private readonly imageCompressService: ImageCompressService,
    private readonly toastService: ToastService,
    private readonly location: Location,
    private readonly cdr: ChangeDetectorRef,
    private readonly fileDataService: FileDataService,
    private readonly documentExtractDataService: DocumentExtractDataService,
    private readonly driverQualificationService: DriverQualificationService
  ) {
    this.initializeUploadState();
  }

  private initializeUploadState(): void {
    // Refresh license data from server to ensure we have the latest state
    this.registrationService.getLicense(true).subscribe({
      next: (license) => {
        this.setUploadStateBasedOnLicense(license);
      },
      error: (error) => {
        console.error('Error loading license data:', error);
        // Fallback to stored license data
        const storedLicense = this.registrationService.getLicenseStore();
        this.setUploadStateBasedOnLicense(storedLicense);
      }
    });
  }

  private setUploadStateBasedOnLicense(license: any): void {
    // If both images exist, navigate to license section immediately
    if (license?.frontScanImagePresent && license?.backScanImagePresent) {
      // Both images are uploaded, redirect to license section
      setTimeout(() => {
        this.navigateToLicense();
      }, 100);
      return;
    }
    
    // If front image exists but back doesn't, start with back upload
    if (license?.frontScanImagePresent && !license?.backScanImagePresent) {
      this.isFrontSave = true;
    } else {
      // Default to front upload (no images or only back exists)
      this.isFrontSave = false;
    }
  }

  getHeaderTitle(): string {
    if (this.finalImageFiles?.length === 2) {
      return 'Review';
    }
    return this.isFrontSave ? 'Upload Back License' : 'Upload Front License';
  }

  openGallery(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  async onProcessPictureFile(event: any) {
    if (!event.target.files?.length) {
      return;
    }

    try {
      const [file] = event.target.files;

      if (!file.type.startsWith('image/')) {
        this.toastService.showError('Please select a valid image file');
        return;
      }

      // Handle large files
      let processedFile = file;
      if (file.size > MAX_FILE_SIZE) {
        processedFile = await this.imageCompressService.compressImage(file, 0.6);
      }

      // Document Extract API integration (only for front license)
      if (!this.isFrontSave) {
        await this.extractLicenseData(processedFile);
      }

      // Continue with normal flow
      this.handleImageFile(processedFile, event);
    } catch (error) {
      this.toastService.showError('Error processing image. Please try again.');
      this.resetFileInput();
    }
  }

  private handleImageFile(file: File, originalEvent: any) {
    const extension = file.name
      .split('.')
      .pop()
      ?.toLowerCase();
    
    // Use the same format handling as profile component
    const fileType = file.type;
    if (fileType.startsWith('image/')) {
      this.imageFormat = extension ? extension : fileType.split('/')[1];
    }

    // Use the exact same approach as profile component
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    if (file) {
      reader.onload = () => {
        this.documentPicture = file;
      };
    }
    
    // Set states exactly like profile component
    this.loading = true;
    this.showCrop = true;
    
    // Use the original event directly - this is the key difference!
    this.imageChangedEvent = originalEvent;
  }

  async onImageCropped(event: ImageCroppedEvent) {
    // Use the same condition as profile component
    if (event && (event.base64 || event.blob)) {
      let name = this.isFrontSave ? BACK_LICENSE_DEFAULT_NAME : FRONT_LICENSE_DEFAULT_NAME;
      
      try {
        let imageFile: File;
        
        if (event.blob) {
          // Use blob directly - more efficient (same as profile component)
          imageFile = new File([event.blob], name, { type: this.imageFormat || 'image/png' });
        } else if (event.base64) {
          // Fallback to base64 (same as profile component)
          imageFile = new File([base64ToFile(event.base64)], name, { type: this.imageFormat || 'image/png' });
        }

        // Use the same approach as profile component - compress then read
        const compressedFile = await this.imageCompressService.compressImage(imageFile, 1);

        let reader = new FileReader();
        reader.readAsDataURL(compressedFile);
        reader.onload = readerEvent => {
          const url = readerEvent.target.result as string;
          
          // Store in both the specific image variables and the capturedImage (for profile component compatibility)
          this.capturedImage = compressedFile;
          this.capturedImageUrl = this.domSanitizer.bypassSecurityTrustUrl(url);
          
          if (!this.isFrontSave) {
            this.frontImage = compressedFile;
            this.frontImageUrl = this.domSanitizer.bypassSecurityTrustUrl(url);
          } else {
            this.backImage = compressedFile;
            this.backImageUrl = this.domSanitizer.bypassSecurityTrustUrl(url);
          }
        };
        
      } catch (error) {
        this.toastService.showError('Unable to process image, Please try again');
        this.onCancelCrop();
      }
    }
  }

  private async optimizeImageForPreview(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        try {
          // Clean up object URL
          URL.revokeObjectURL(objectUrl);
          
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height && width > this.PREVIEW_IMAGE_MAX_SIZE) {
            height = (height * this.PREVIEW_IMAGE_MAX_SIZE) / width;
            width = this.PREVIEW_IMAGE_MAX_SIZE;
          } else if (height > this.PREVIEW_IMAGE_MAX_SIZE) {
            width = (width * this.PREVIEW_IMAGE_MAX_SIZE) / height;
            height = this.PREVIEW_IMAGE_MAX_SIZE;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with reduced quality for preview
          canvas.toBlob(
            blob => {
              if (blob) {
                resolve(new File([blob], file.name, { type: 'image/jpeg' }));
              } else {
                reject(new Error('Failed to create preview image'));
              }
            },
            'image/jpeg',
            0.7 // 70% quality
          );
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          reject(error);
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };
      
      img.src = objectUrl;
    });
  }

  onPreviewImageLoad() {
    this.imageLoadCount++;
    if (this.imageLoadCount >= 2) {
      // Both images loaded
      this.isPreviewLoading = false;
      this.imageLoadCount = 0;
    }
  }

  onPreviewImageError() {
    this.toastService.showError('Error loading preview image');
    this.isPreviewLoading = false;
    this.imageLoadCount = 0;
  }

  onImageLoaded() {
    try {
      this.loading = false;
    } catch (error) {
      this.toastService.showError('Error loading image. Please try again.');
      this.resetLicense();
    }
  }

  onCropperReady() {
    this.loading = false;
  }

  private handleRotation(direction: number) {
    if (this.isRotating) return;

    this.isRotating = true;
    this.loading = true;

    if (this.rotationTimeout) {
      clearTimeout(this.rotationTimeout);
    }

    requestAnimationFrame(() => {
      this.canvasRotation += direction;
      this.flipAfterRotate();

      this.rotationTimeout = setTimeout(() => {
        this.isRotating = false;
        this.loading = false;
      }, 300);
    });
  }

  rotateLeft() {
    this.handleRotation(-1);
  }

  rotateRight() {
    this.handleRotation(1);
  }

  private flipAfterRotate() {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH
    };
  }

  onFinishCrop() {
    if (this.capturedImage) {
      this.saveLicense();
    } else {
      this.toastService.showError('No image to save. Please try again.');
      this.onCancelCrop();
    }
  }

  onCancelCrop() {
    this.showCrop = false;
    this.loading = false;

    if (!this.isFrontSave) {
      this.frontImage = undefined;
      this.frontImageUrl = undefined;
    } else {
      this.backImage = undefined;
      this.backImageUrl = undefined;
    }

    // Clear the image changed event
    this.imageChangedEvent = null;
    // Reset rotation state
    this.canvasRotation = 0;
    this.transform = {};
    this.resetFileInput();
  }

  resetLicense() {
    this.isFrontSave = false;
    this.finalImageFiles = [];
    this.frontImage = undefined;
    this.frontImageUrl = undefined;
    this.backImage = undefined;
    this.backImageUrl = undefined;
    this.showCrop = false;
    this.loading = false;
    this.isPreviewLoading = false;
    this.imageLoadCount = 0;
    // Reset rotation state
    this.canvasRotation = 0;
    this.transform = {};
    this.resetFileInput();
  }

  private resetFileInput() {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onLoadImageFailed() {
    this.loading = false;
    this.toastService.showError('Failed to load image. Please try again');
    this.resetFileInput();
  }

  private readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  }

  saveLicense(): void {
    if (!this.capturedImage) {
      this.toastService.showError('No image to save.');
      return;
    }

    this.isApiLoading = true;

    let saveModel: any = {};
    if (!this.isFrontSave) {
      // Uploading front image
      saveModel.frontScanImageExtension = getDocumentFileExtension(this.capturedImage);
    } else {
      // Uploading back image
      saveModel.backScanImageExtension = getDocumentFileExtension(this.capturedImage);
    }
    
    const licensePresent = this.isLicensePresent();

    if (licensePresent) {
      const license = this.registrationService.getLicenseStore();
      
      this.licenseDataService
        .updateLicense(saveModel, license.id)
        .pipe(
          first(),
          tap(response => {
            if (response) {
              // Update the license state with the new image presence
              if (!this.isFrontSave) {
                response.frontScanImagePresent = true;
              } else {
                response.backScanImagePresent = true;
              }
              this.registrationService.updateLicenseStore(response);
              this.uploadLicenseImage(response, this.capturedImage);
            } else {
              this.toastService.showError('Failed to update license. Please try again.');
              this.isApiLoading = false;
            }
          }),
          catchError(error => {
            this.toastService.showError('Failed to update license. Please try again.');
            this.isApiLoading = false;
            return of(false);
          })
        )
        .subscribe();
    } else {
      this.licenseDataService
        .uploadLicense(saveModel)
        .pipe(
          first(),
          tap(response => {
            if (response) {
              // Update the license state with the new image presence
              if (!this.isFrontSave) {
                response.frontScanImagePresent = true;
              } else {
                response.backScanImagePresent = true;
              }
              this.registrationService.updateLicenseStore(response);
              this.uploadLicenseImage(response, this.capturedImage);
            } else {
              this.toastService.showError('Failed to create license. Please try again.');
              this.isApiLoading = false;
            }
          }),
          catchError(error => {
            this.toastService.showError('Failed to upload license. Please try again');
            this.isApiLoading = false;
            return of(false);
          })
        )
        .subscribe();
    }
  }

  uploadLicenseImage(response: any, licenseImage: File): void {
    if (!response) {
      this.toastService.showError('Invalid response from license update.');
      this.isApiLoading = false;
      return;
    }

    let licenseUrl: any;
    let licenseNewName: any;
    if (!this.isFrontSave) {
      // Uploading front image
      licenseUrl = response.frontScanImageUrl;
      licenseNewName = response.frontScanImageFile;
    } else {
      // Uploading back image
      licenseUrl = response.backScanImageUrl;
      licenseNewName = response.backScanImageFile;
    }

    if (!licenseUrl || !licenseNewName) {
      this.toastService.showError('Failed to get upload URL. Please try again.');
      this.isApiLoading = false;
      return;
    }

    const fileExtension = licenseNewName.split('.').pop()?.toLowerCase() || '';
    let contentType: string;
    
    try {
      contentType = (mime as any).getType(fileExtension) || 'image/png';
    } catch (error) {
      // Fallback to manual content type mapping
      switch (fileExtension) {
        case 'jpg':
        case 'jpeg':
          contentType = 'image/jpeg';
          break;
        case 'png':
          contentType = 'image/png';
          break;
        case 'gif':
          contentType = 'image/gif';
          break;
        case 'webp':
          contentType = 'image/webp';
          break;
        default:
          contentType = 'image/png';
      }
    }
    
    const newFile = new File([licenseImage], licenseNewName, { type: contentType });
    const fileContentType = newFile.type ? newFile.type : 'image/png';
    
    this.fileDataService
      .uploadFile(newFile, licenseUrl, fileContentType)
      .pipe(
        first(),
        tap(uploadResponse => {
          if (uploadResponse) {
            // Success - reset UI state and show success message
            this.resetUploadState();
            
            if (!this.isFrontSave) {
              // Just uploaded front image
              this.finalImageFiles.push(this.frontImage || this.capturedImage);
              this.toastService.showSuccess('Front license image uploaded successfully!');

              // After uploading front image, refresh license data from backend to get updated upload dates
              this.registrationService.getLicense(true).subscribe(updatedLicense => {
                if (updatedLicense) {
                  // Update the store with fresh license data including upload dates
                  this.registrationService.updateLicenseStore(updatedLicense);
                }

                // Check if back image is required and doesn't exist
                if (!updatedLicense?.backScanImagePresent) {
                  // Check if both images are required or if front is sufficient
                  // For most jurisdictions, both front and back are required
                  // Switch to back image upload mode
                  this.isFrontSave = true;
                  this.cdr.detectChanges();
                } else {
                  // Back image already exists, navigate to license section
                  this.navigateToLicense();
                }
              });
            } else {
              // Just uploaded back image, add to finalImageFiles and finish
              this.finalImageFiles.push(this.backImage || this.capturedImage);
              this.toastService.showSuccess('License uploaded successfully!');
              const backFile = this.backImage || this.capturedImage;
              const frontFile = this.finalImageFiles[0];
              const frontExt = frontFile?.name?.split('.').pop()?.toLowerCase() || fileExtension || 'png';
              if (frontFile) {
                this.registrationService.notifyCarriersOnDocumentUpload('DriverLicense', frontExt, frontFile, 'Driver License (Front)');
              }
              this.registrationService.notifyCarriersOnDocumentUpload('DriverLicense', fileExtension || 'png', backFile, 'Driver License (Back)');

              // Refresh license data from backend to ensure upload dates are updated in store
              this.registrationService.getLicense(true).subscribe(updatedLicense => {
                if (updatedLicense) {
                  // Update the store with fresh license data including upload dates
                  this.registrationService.updateLicenseStore(updatedLicense);
                }
                this.navigateToLicense();
              });
            }
          } else {
            this.toastService.showError('Failed to upload image. Please try again.');
          }
        }),
        catchError(error => {
          this.toastService.showError('Failed to upload image. Please try again.');
          return of(false);
        }),
        finalize(() => {
          this.isApiLoading = false;
        })
      )
      .subscribe();
  }

  resetUploadState(): void {
    this.showCrop = false;
    this.loading = false;
    this.capturedImage = null;
    this.capturedImageUrl = null;
    this.imageChangedEvent = null;
    // Reset rotation state
    this.canvasRotation = 0;
    this.transform = {};
    this.resetFileInput();
  }

  onNavigateBack(): void {
    // If we're in back image upload mode but came from front upload, reset to front mode
    if (this.isFrontSave && !this.showCrop && this.finalImageFiles.length === 0) {
      this.isFrontSave = false;
      this.cdr.detectChanges();
    } else {
      this.location.back();
    }
  }
  navigateToProfile() {
    this.registrationService.navigateToProfile();
  }
  
  navigateToLicense() {
    this.router.navigate(['profile/license']);
  }

  private uploadLicenseImages(response: any, licenseFile: File) {
    if (licenseFile.name === FRONT_LICENSE_DEFAULT_NAME) {
      this.licenseDataService
        .uploadFrontLicenseImage(licenseFile, response)
        .pipe(
          first(),
          catchError(error => {
            this.toastService.showError('Failed to upload license image. Please try again');
            return of(false);
          }),
          finalize(() => {
            this.isApiLoading = false;
          })
        )
        .subscribe();
    } else if (licenseFile.name === BACK_LICENSE_DEFAULT_NAME) {
      this.licenseDataService
        .uploadBackLicenseImage(licenseFile, response)
        .pipe(
          first(),
          tap(() => {
            this.toastService.showSuccess('License uploaded successfully');
            this.navigateToLicense();
          }),
          catchError(error => {
            this.toastService.showError('Failed to upload license image. Please try again');
            return of(false);
          }),
          finalize(() => {
            this.isApiLoading = false;
          })
        )
        .subscribe();
    }
  }
  private isLicensePresent() {
    const license = this.registrationService.getLicenseStore();
    return license?.frontScanImagePresent;
  }

  /**
   * Extract license data using Document Extract API
   */
  private async extractLicenseData(file: File): Promise<void> {
    this.isExtracting = true;
    this.cdr.detectChanges();

    return new Promise<void>((resolve) => {
      this.documentExtractDataService
        .extractDocument(file, 'DriverLicense')
        .pipe(
          first(),
          finalize(() => {
            this.isExtracting = false;
            this.cdr.detectChanges();
            resolve();
          })
        )
        .subscribe({
          next: (response) => {
            if (response && response.extracted_data) {
              this.extractedLicenseData = response.extracted_data;
              // Store in registration service for later use by license form
              this.registrationService.storeExtractedLicenseData(response.extracted_data);
            }
          },
          error: (error) => {
            // Silent fallback - just continue without extraction
            console.error('Document extraction failed:', error);
            this.extractedLicenseData = null;
          }
        });
    });
  }

  ngOnDestroy() {
    // Clear timeouts and memory
    if (this.rotationTimeout) {
      clearTimeout(this.rotationTimeout);
    }

    // Clear object URLs
    if (this.frontImageUrl) {
      URL.revokeObjectURL(this.frontImageUrl.toString());
    }
    if (this.backImageUrl) {
      URL.revokeObjectURL(this.backImageUrl.toString());
    }

    // Clear file data
    this.frontImage = null;
    this.backImage = null;
    this.documentPicture = null;
    this.imageChangedEvent = null;
  }
}
